// The frame every OT screen renders inside: the IPD theme, the page header,
// and the shared loading / error states.
//
// Navigation lives in `OtShell` (the module's sidebar), not here — the same
// split Emergency uses. Reusing the IPD toolkit rather than introducing a second
// design system is ADR-0008.

import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";

import { IPD_COLORS, IpdPageHeader, createIpdTheme, ipdPageSx } from "../../ipd/utils";

/**
 * Catches a render crash so it shows as a message instead of a white page.
 *
 * A CRA app has no error boundary of its own: an uncaught error thrown while
 * rendering unmounts the ENTIRE tree, and the user gets a blank browser window
 * with nothing on it and nothing in the UI to say why. That is what a shape
 * mismatch between an endpoint and a panel looked like -- the tab rendered
 * while its data was still null, then went blank the instant the data arrived.
 *
 * This does not make the bug go away. It makes it legible: the screen says
 * something broke and offers a reload, and the real error still reaches the
 * console for whoever has to fix it.
 */
class OtErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept: this is the message that identifies the file and line.
    console.error("[OT] a screen failed to render", error, info?.componentStack);
  }

  componentDidUpdate(prev) {
    // Moving to another tab or case should not stay broken because the last
    // one was.
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ color: IPD_COLORS.error.text, fontWeight: 700, mb: 1 }}>
          Application Encountered a Display Error
        </Typography>
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5, mb: 2 }}>
          {String(this.state.error?.message || this.state.error)}
        </Typography>
        <Button
          onClick={() => window.location.reload()}
          variant="outlined"
          startIcon={<RefreshIcon />}
          sx={{ color: IPD_COLORS.primary, borderColor: IPD_COLORS.primary }}
        >
          Reload
        </Button>
      </Box>
    );
  }
}

export default function OtScreen({
  eyebrow = "Operation Theatre",
  title,
  description,
  badge,
  actions,
  loading,
  error,
  onRetry,
  children,
}) {
  return (
    <ThemeProvider theme={createIpdTheme()}>
      {/* The page padding is not optional: the shell's sidebar sits immediately
          to the left, and its collapse chevron overhangs 13px into this area.
          Without the gutter the heading starts flush against the sidebar and
          the chevron covers its first character. Every Emergency screen adds
          the same `p: { xs: 2, md: 3 }` for the same reason. */}
      <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
        <IpdPageHeader
          eyebrow={eyebrow}
          title={title}
          description={description}
          badge={badge}
          actions={actions}
        />
        {error ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ color: IPD_COLORS.error.text, mb: 2 }}>{error}</Typography>
            {onRetry && (
              <Button
                onClick={onRetry}
                variant="outlined"
                startIcon={<RefreshIcon />}
                sx={{ color: IPD_COLORS.primary, borderColor: IPD_COLORS.primary }}
              >
                Retry
              </Button>
            )}
          </Box>
        ) : loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <OtErrorBoundary resetKey={title}>{children}</OtErrorBoundary>
        )}
      </Box>
    </ThemeProvider>
  );
}
